import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmActivateModal from "components/Modals/ConfirmActivateModal";
import customFields from "services/apis/customFields";
import rulesApi from "services/apis/rulesApi";
import routeUserApi from "services/apis/routeUserApi";
import {
  RuleMessageObjectType,
  RuleMessageType,
  RuleVersionCrudType,
} from "../types";
import { defaultRuleMessageObject, defaultRuleObject } from "../types/defaults";
import { RuleFormType } from "../validation/ruleFormSchema";
import useVersionRuleForm from "../hooks/useVersionRuleForm";
import { MANUAL_ROUTING_LABEL, MANUAL_RULE_TYPE_LABEL } from "constants/index";
import RuleBuilderConvert from "utils/converter/RuleBuilderConvert";
import AICCRuleBuilderUtils from "utils/AICCRuleBuilderUtils";
import ToastUtils from "utils/ToastUtils";

export const RuleDataContext = React.createContext({
  query: [],
  groupData: [],
  ruleTypes: [],
  routingUsers: [],
  isFormChanged: false,
  type: RuleVersionCrudType.CREATE,
  isLoading: true,
  ruleQueryString: "",
  onSubmitRule: (isOverrite: boolean) => {},
  setQuery: (query) => {},
  getNextPriorityForRule: async () => {},
});

export const RuleDataProvider = (props) => {
  const params = useParams();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("t") as RuleVersionCrudType;
  const { versionId, overviewId: rulesId } = params;
  const [query, setQuery] = useState([AICCRuleBuilderUtils.defaultQuery()]);
  const [groupData, setGroupData] = useState([]);
  const [ruleTypes, setRuleTypes] = useState([]);
  const [routingUsers, setRoutingUsers] = useState([]);
  const [ruleData, setRuleData] = useState<RuleFormType>(defaultRuleObject);
  const [ruleQueryString, setRuleQueryString] = useState("");
  const { methods, isFormChanged } = useVersionRuleForm(ruleData);
  const [isLoading, setIsLoading] = useState(!!rulesId);
  const [manualRoutingTypeId, setManualRoutingTypeId] = useState("");
  const [isRulePrioritySame, setIsRulePrioritySame] = useState(false);
  const { reset, getValues } = methods;

  const { datastream } = useSelector(
    (state) => (state as any)?.datastream?.selectedDS
  );

  const getCustomFieldsByDataStreamConfigId = async () => {
    const dataStreamConfigId = datastream?.dataStreamConfigId;
    if (!dataStreamConfigId) {
      return;
    }
    try {
      const response = await customFields.getCustomFieldsByDataStreamConfigId({
        dataStreamConfigId,
      });

      setGroupData(response.data);
    } catch (error) {
      console.log(error);
    }
  };
  const getAllRuleTypes = async () => {
    try {
      const response = await rulesApi.getAllRulesType({
        dsConfigVersionId: versionId,
      });
      const data = response?.data;
      const sortedRules = data?.sort(function (a, b) {
        return a.rulesOrder - b.rulesOrder;
      });
      const ruleTypeData = sortedRules.reduce(
        (acc, type) => {
          if (type.isActive) {
            acc.types.push(type);
          }
          if (type?.label?.toLowerCase() === MANUAL_ROUTING_LABEL) {
            acc.manualRoutingTypeId = type.rulesTypeId;
          }
          return { ...acc };
        },
        { types: [], manualRoutingTypeId: null }
      );
      setRuleTypes(ruleTypeData.types);
      setManualRoutingTypeId(ruleTypeData.manualRoutingTypeId);
    } catch (error) {
      console.error(error);
    }
  };
  const fetchRuleDetails = async () => {
    try {
      const response = await rulesApi.getRulesById({
        rulesId: params.overviewId,
      });
      let selectedMessageType = RuleMessageType.INTERNAL;
      let messageObject: RuleMessageObjectType =
        response?.data?.messages?.reduce((acc, message) => {
          if (message.isMessageActive) {
            selectedMessageType = message.messageType;
          }
          return {
            ...acc,
            [message.messageType]: message,
          };
        }, {});
      if (!Object.keys(messageObject).length) {
        messageObject = defaultRuleMessageObject;
      }
      setRuleData({
        ruleName: response?.data?.name,
        groupName: response?.data?.groupName,
        priority: response?.data?.priority,
        isActive: response?.data?.isActive,
        rulesDescription: response?.data?.rulesDescription,
        rulesTypeId: response?.data?.rulesTypeId.toString(),
        routingUserId: response?.data?.routingUserId,
        messages: messageObject,
      });

      if (
        response.data?.whereClauses &&
        response.data?.whereClauses.length > 0
      ) {
        setRuleQueryString(response.data?.queryString);

        const respQuery = RuleBuilderConvert.aiccRBWhereClauseToQuery(
          response.data.whereClauses
        );

        setQuery(respQuery);
      }
    } catch (error) {
      console.error(error);
    }
  };

  async function getRoutingUserByDataStreamConfigId() {
    try {
      const response = await routeUserApi.getAllRouteUserByDataStreamConfigId({
        dataStreamConfigId: datastream.dataStreamConfigId,
      });
      setRoutingUsers(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  async function getNextPriorityForRule(isActive = true) {
    try {
      const response = await rulesApi.getNextPriorityForRules({
        dsConfigVersionId: versionId,
      });
      setRuleData((prev) => {
        return {
          ...prev,
          priority: response?.data?.nextPriority,
          isActive,
        };
      });
      return response?.data?.nextPriority;
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const getRuleData = async () => {
      const dataLoaders = [
        getCustomFieldsByDataStreamConfigId(),
        getAllRuleTypes(),
        getRoutingUserByDataStreamConfigId(),
      ];
      if (
        type === RuleVersionCrudType.EDIT ||
        type === RuleVersionCrudType.VIEW
      ) {
        dataLoaders.push(fetchRuleDetails());
      } else {
        dataLoaders.push(getNextPriorityForRule());
      }
      setIsLoading(true);
      Promise.all(dataLoaders)
        .catch((err) => {
          console.error("API error:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    getRuleData();
  }, []);

  useEffect(() => {
    if (ruleTypes.length) {
      let rulesTypeId = ruleData?.rulesTypeId?.toString();
      if (ruleData?.routingUserId) {
        rulesTypeId = ruleTypes
          .find(
            (ruleType) =>
              ruleType.label?.toLowerCase() === MANUAL_RULE_TYPE_LABEL
          )
          ?.rulesTypeId?.toString();
      }
      setRuleData((prev) => ({ ...prev, rulesTypeId }));
    }
  }, [ruleTypes, ruleData?.routingUserId]);

  useEffect(() => {
    if (ruleData) {
      reset(ruleData);
    }
  }, [ruleData, reset]);

  const onSubmitRule = async (isOverrite = false) => {
    const {
      ruleName,
      isActive,
      routingUserId,
      groupName,
      priority,
      rulesTypeId,
      rulesDescription,
      messages,
    } = getValues();
    let selectedRuleTypeId = "";
    if (routingUserId !== null) {
      selectedRuleTypeId = manualRoutingTypeId;
    } else {
      selectedRuleTypeId = rulesTypeId;
    }
    const whereClauses = RuleBuilderConvert.aiccRBQueryToWhereClause(query);

    const createRequest = {
      dsConfigVersionId: versionId,
      isDeleted: false,
      name: ruleName,
      isActive: isActive,
      queryString: "",
      routingUserId,
      rulesGroupId: 0,
      rulesId: rulesId ? rulesId : 0,
      rulesOrder: "",
      groupName: groupName,
      priority: priority,
      rulesTypeId: Number(selectedRuleTypeId),
      rulesDescription: rulesDescription,
      messageList: Object.values(messages),
      isMessageActive: true,
      whereClauses,
    };

    if (typeof isOverrite === "boolean" && isOverrite) {
      createRequest["isOverrite"] = isOverrite;
    }

    try {
      const response = await rulesApi.saveOrUpdateRules(createRequest);
      if (response.status === 200) {
        if (response.data.status != 200) {
          ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
        } else {
          toast.success(t(`statusCodes.${response?.data?.code}`));
        }
        navigate(-1);
      } else {
        // toast.error(t("versionsForm.errorMessage.ruleSaveFailure"));
      }
    } catch (error) {
      if (error.response.data.isDSConfigVersionPriorityAlready) {
        setIsRulePrioritySame(
          error.response.data.isDSConfigVersionPriorityAlready
        );
      } else if (error.response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${error.response.data.code}`));
      }
      toast.error(error.response?.data);
    }
  };

  return (
    <RuleDataContext.Provider
      value={{
        type,
        query,
        groupData,
        ruleTypes,
        routingUsers,
        isFormChanged,
        isLoading,
        ruleQueryString,
        onSubmitRule,
        setQuery,
        getNextPriorityForRule,
      }}
      {...props}
    >
      <FormProvider {...methods}>
        <form className="space-y-4 p-4 border rounded-md">
          {props.children}
        </form>
      </FormProvider>
      <ConfirmActivateModal
        show={isRulePrioritySame}
        onOk={() => {
          onSubmitRule(true);
          setIsRulePrioritySame(false);
        }}
        onClose={() => {
          setIsRulePrioritySame(false);
        }}
        messageTitles={t("createRuleEngine.modalErrorMessage")}
      />
    </RuleDataContext.Provider>
  );
};

export const useRuleDataContext = () => {
  const queryContextProps = useContext(RuleDataContext);

  return {
    ...queryContextProps,
  };
};
